import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { LocalizedName, localizedName } from '@/components/localized-name';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import { originLabelKey } from '@/features/manage-subscriptions/subscription-cells';
import {
  useBookingLocations,
  useSubscriptionPlans,
  type SubscriptionSearchModel,
} from '@/features/manage-subscriptions/subscriptions.api';
import {
  DATE_BASIS,
  ORIGIN,
  PAYMENT_STATE,
  STATUS_FILTER,
} from '@/features/manage-subscriptions/types';
import {
  Meter,
  SectionTitle,
  StatGroup,
  StatTile,
} from '@/components/ui/stat-tile';
import { formatCurrency, formatNumber } from '@/lib/format';
import { GiftRowsSheet } from './gift-rows-sheet';
import { SubscriptionRowsSheet } from './subscription-rows-sheet';
import { useMembershipDashboard } from './subscription-sales.api';
import { toApiDate } from '@/lib/store-time';

/**
 * Subscription sales — what the memberships business did, and which plans did
 * it.
 *
 * Was the Overview and Plans tabs of the old `/memberships` page. Splitting the
 * day-to-day grid out to Management left these two without a home; they are a
 * report, so they belong here.
 *
 * No date picker, following the original: the page answers "what is true right
 * now", and a range plus a counted-by selector was four ways to misread a
 * number. The API still takes a window, so the page sends the current month and
 * says so.
 */

const ANY = 'ANY';

function monthWindow() {
  const now = new Date();
  const day = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  // Through the shared helper so the window is the store's month, not the
  // browser's: building it from local fields used whatever zone the admin's
  // machine is in instead of the configured offset.
  return { startDate: toApiDate(day(first)), endDate: toApiDate(day(last), true) };
}

/** One figure of a rollup row, label above value. */
function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-sm tabular-nums">{value}</p>
    </div>
  );
}

export function SubscriptionSalesPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [locationId, setLocationId] = useState(ANY);
  const [subscriptionId, setSubscriptionId] = useState(ANY);
  const [status, setStatus] = useState(String(STATUS_FILTER.all));
  const [origin, setOrigin] = useState(ANY);
  const [paymentState, setPaymentState] = useState(String(PAYMENT_STATE.all));

  const { data: locations } = useBookingLocations();
  const { data: plans } = useSubscriptionPlans();

  const period = useMemo(() => monthWindow(), []);

  const model: SubscriptionSearchModel = useMemo(
    () => ({
      ...period,
      dateBasis: DATE_BASIS.created,
      locationId: locationId === ANY ? null : locationId,
      subscriptionId: subscriptionId === ANY ? null : subscriptionId,
      // Unlike most filters here, 0 means "all" rather than being omitted.
      status: Number(status),
      origin: origin === ANY ? null : Number(origin),
      paymentState: Number(paymentState),
      autoRenew: null,
      includeMemberSeats: true,
      query,
      sort: 0,
      page: 0,
      size: 20,
    }),
    [period, locationId, subscriptionId, status, origin, paymentState, query]
  );

  const dashboard = useMembershipDashboard(model);
  const loading = dashboard.isPending;
  // A refresh keeps the figures on screen and dims them; only a first
  // load, with nothing to dim, gets skeletons.
  const busy = dashboard.isFetching && !dashboard.isPending;

  const people = dashboard.data?.people;
  const originStats = dashboard.data?.origin;
  const money = dashboard.data?.money;
  const offlineByMethod = dashboard.data?.offlineByMethod ?? [];
  const planRows = dashboard.data?.plans ?? [];

  /**
   * Opening a tile is a question about that number, so it answers in place: a
   * drawer of the rows behind it, built from the same model the tile was, with
   * only the tile's own filter patched over it. The management grid is one
   * click away inside the drawer, for when the answer turns into work.
   */
  const [drill, setDrill] = useState<{
    title: string;
    patch: Partial<SubscriptionSearchModel>;
    /** False where the grid has no control that reproduces this view. */
    linkable?: boolean;
  } | null>(null);
  const [giftsOpen, setGiftsOpen] = useState(false);

  /** The same filter, on the page that can act on it. */
  function subscriptionsLink(patch: Partial<SubscriptionSearchModel>) {
    const params = new URLSearchParams();
    if (locationId !== ANY) params.set('locationId', locationId);
    if (subscriptionId !== ANY) params.set('subscriptionId', subscriptionId);
    if (patch.status !== undefined && patch.status !== null) {
      params.set('status', String(patch.status));
    }
    if (patch.origin !== undefined && patch.origin !== null) {
      params.set('origin', String(patch.origin));
    }
    if (patch.paymentState !== undefined && patch.paymentState !== null) {
      params.set('paymentState', String(patch.paymentState));
    }
    return `/booking/manage/subscriptions?${params}`;
  }

  const filterFields: FilterField[] = [
    {
      id: 'location',
      label: t('subscriptionSales.location'),
      value: locationId,
      onChange: setLocationId,
      options: [
        { value: ANY, label: t('subscriptionSales.any') },
        ...(locations ?? []).map((l) => ({
          value: l.id,
          label: localizedName(l, language) || (l.name ?? ''),
        })),
      ],
    },
    {
      id: 'plan',
      label: t('subscriptionSales.membership'),
      value: subscriptionId,
      onChange: setSubscriptionId,
      options: [
        { value: ANY, label: t('subscriptionSales.any') },
        ...(plans ?? []).map((p) => ({
          value: p.id,
          label: localizedName(p, language),
        })),
      ],
    },
    {
      id: 'status',
      label: t('subscriptionSales.status'),
      value: status,
      onChange: setStatus,
      sort: false,
      resetValue: String(STATUS_FILTER.all),
      options: [
        { value: String(STATUS_FILTER.all), label: t('subscriptionSales.all') },
        { value: String(STATUS_FILTER.active), label: t('subscriptionSales.active') },
        { value: String(STATUS_FILTER.expiring), label: t('subscriptionSales.expiringSoon') },
        { value: String(STATUS_FILTER.expired), label: t('subscriptionSales.expired') },
        { value: String(STATUS_FILTER.cancelled), label: t('subscriptionSales.cancelled') },
      ],
    },
    {
      id: 'origin',
      label: t('subscriptionSales.originLabel'),
      value: origin,
      onChange: setOrigin,
      sort: false,
      options: [
        { value: ANY, label: t('subscriptionSales.any') },
        ...Object.values(ORIGIN).map((value) => ({
          value: String(value),
          label: t(originLabelKey(value)),
        })),
      ],
    },
    {
      id: 'paymentState',
      label: t('subscriptionSales.paidHow'),
      value: paymentState,
      onChange: setPaymentState,
      sort: false,
      resetValue: String(PAYMENT_STATE.all),
      options: [
        { value: String(PAYMENT_STATE.all), label: t('subscriptionSales.any') },
        { value: String(PAYMENT_STATE.paidOnline), label: t('subscriptionSales.paidOnline') },
        { value: String(PAYMENT_STATE.paidOffline), label: t('subscriptionSales.paidOffline') },
        { value: String(PAYMENT_STATE.belowList), label: t('subscriptionSales.belowList') },
        { value: String(PAYMENT_STATE.unpaid), label: t('subscriptionSales.unbilled') },
        { value: String(PAYMENT_STATE.sponsored), label: t('subscriptionSales.sponsored') },
      ],
    },
  ];

  /** A totals row that vanishes on a phone is how a number gets reported wrong. */
  const totals = planRows.reduce(
    (sum, row) => ({
      sold: sum.sold + (row.sold ?? 0),
      active: sum.active + (row.active ?? 0),
      paidOnline: sum.paidOnline + (row.paidOnline ?? 0),
      paidOffline: sum.paidOffline + (row.paidOffline ?? 0),
      sponsored: sum.sponsored + (row.sponsored ?? 0),
      unpaid: sum.unpaid + (row.unpaid ?? 0),
      collected: sum.collected + (row.collected ?? 0),
      unbilled: sum.unbilled + (row.unbilled ?? 0),
    }),
    {
      sold: 0,
      active: 0,
      paidOnline: 0,
      paidOffline: 0,
      sponsored: 0,
      unpaid: 0,
      collected: 0,
      unbilled: 0,
    }
  );

  const offlineSummary = offlineByMethod
    .map(
      (entry) => `${t(paymentMethodLabelKey(entry.method))} ${formatNumber(entry.count)}`
    )
    .join(' · ');

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('subscriptionSales.title')}
        description={t('subscriptionSales.subtitle')}
      />

      <FilterBar
        busy={dashboard.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query) void dashboard.refetch();
              setQuery(draft);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('subscriptionSales.searchPlaceholder')}
                aria-label={t('subscriptionSales.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          <p className="text-muted-foreground text-sm">
            {t('subscriptionSales.thisMonthNotice')}
          </p>
        }
      />

      {dashboard.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('subscriptionSales.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void dashboard.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      <Tabs defaultValue="overview" className="gap-5">
        <TabsList className="sm:w-auto">
          <TabsTrigger value="overview" className="sm:flex-none sm:px-5">
            {t('subscriptionSales.overview')}
          </TabsTrigger>
          <TabsTrigger value="plans" className="sm:flex-none sm:px-5">
            {t('subscriptionSales.memberships')}
          </TabsTrigger>
        </TabsList>

        {/* ================================================ overview ==== */}
        <TabsContent value="overview" className="space-y-5">
          <StatGroup title={t('subscriptionSales.whosSubscribed')}>
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('subscriptionSales.activeNow')}
              value={formatNumber(people?.activeNow)}
              sub={t('subscriptionSales.ofNEver', {
                count: people?.totalAllTime ?? 0,
              })}
              onClick={() =>
                setDrill({
                  title: t('subscriptionSales.activeNow'),
                  patch: { status: STATUS_FILTER.active, paymentState: PAYMENT_STATE.all },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="primary"
              label={t('subscriptionSales.newMemberships')}
              value={formatNumber(people?.newInRange)}
              sub={t('subscriptionSales.thisMonth')}
              onClick={() =>
                setDrill({
                  title: t('subscriptionSales.newMemberships'),
                  // The model already counts by creation date over this window,
                  // so "new" is everything in it.
                  patch: {
                    status: STATUS_FILTER.all,
                    origin: null,
                    paymentState: PAYMENT_STATE.all,
                  },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="warn"
              label={t('subscriptionSales.expiringIn30Days')}
              value={formatNumber(people?.expiringIn30Days)}
              onClick={() =>
                setDrill({
                  title: t('subscriptionSales.expiringIn30Days'),
                  patch: { status: STATUS_FILTER.expiring, paymentState: PAYMENT_STATE.all },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('subscriptionSales.expired')}
              value={formatNumber(people?.expiredInRange)}
              sub={t('subscriptionSales.thisMonth')}
              onClick={() =>
                setDrill({
                  title: t('subscriptionSales.expired'),
                  patch: { status: STATUS_FILTER.expired, paymentState: PAYMENT_STATE.all },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t(originLabelKey(ORIGIN.memberSeat))}
              value={formatNumber(people?.memberSeats)}
              sub={t('subscriptionSales.notCountedAsSales')}
              onClick={() =>
                setDrill({
                  title: t(originLabelKey(ORIGIN.memberSeat)),
                  patch: { origin: ORIGIN.memberSeat, status: STATUS_FILTER.all, paymentState: PAYMENT_STATE.all },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('subscriptionSales.autoRenewOn')}
              value={formatNumber(people?.autoRenewOn)}
              onClick={() =>
                setDrill({
                  title: t('subscriptionSales.autoRenewOn'),
                  patch: { autoRenew: true },
                  // `autoRenew` is a field on the search model with no control
                  // on the grid, so the grid cannot show this same set.
                  linkable: false,
                })
              }
            />
          </StatGroup>

          <StatGroup
            title={`${t('subscriptionSales.howTheyGotIt')} · ${t('subscriptionSales.scopeActive')}`}
          >
            <StatTile
              loading={loading}
              busy={busy}
              tone="primary"
              label={t(originLabelKey(ORIGIN.selfPurchase))}
              value={formatNumber(originStats?.selfPurchase)}
              onClick={() =>
                setDrill({
                  title: t(originLabelKey(ORIGIN.selfPurchase)),
                  patch: { origin: ORIGIN.selfPurchase, paymentState: PAYMENT_STATE.all },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="cash"
              label={t('subscriptionSales.paidOffline')}
              value={formatNumber(originStats?.paidOffline)}
              sub={offlineSummary}
              onClick={() =>
                setDrill({
                  title: t('subscriptionSales.paidOffline'),
                  patch: { paymentState: PAYMENT_STATE.paidOffline },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t(originLabelKey(ORIGIN.sponsored))}
              value={formatNumber(originStats?.sponsored)}
              onClick={() =>
                setDrill({
                  title: t(originLabelKey(ORIGIN.sponsored)),
                  patch: { origin: ORIGIN.sponsored, paymentState: PAYMENT_STATE.all },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t(originLabelKey(ORIGIN.adminAssigned))}
              value={formatNumber(originStats?.adminAssigned)}
              onClick={() =>
                setDrill({
                  title: t(originLabelKey(ORIGIN.adminAssigned)),
                  patch: { origin: ORIGIN.adminAssigned, paymentState: PAYMENT_STATE.all },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t(originLabelKey(ORIGIN.gift))}
              value={formatNumber(originStats?.fromGift)}
              onClick={() =>
                setDrill({
                  title: t(originLabelKey(ORIGIN.gift)),
                  patch: { origin: ORIGIN.gift, paymentState: PAYMENT_STATE.all },
                })
              }
            />
          </StatGroup>

          <StatGroup
            title={`${t('subscriptionSales.money')} · ${t('subscriptionSales.thisMonth')}`}
          >
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('subscriptionSales.collected')}
              value={formatCurrency(money?.collected)}
              sub={`${t('subscriptionSales.online')} ${formatCurrency(money?.collectedOnline)} · ${t('subscriptionSales.offline')} ${formatCurrency(money?.collectedOffline)}`}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="bad"
              label={t('subscriptionSales.refunded')}
              value={formatCurrency(money?.refunded)}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('subscriptionSales.net')}
              value={formatCurrency(money?.net)}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="warn"
              label={t('subscriptionSales.unbilled')}
              value={formatCurrency(money?.unbilled)}
              sub={t('subscriptionSales.nWithNoPayment', {
                count: money?.unbilledCount ?? 0,
              })}
              tooltip={t('subscriptionSales.unbilledTooltip')}
              onClick={() =>
                setDrill({
                  title: t('subscriptionSales.unbilled'),
                  patch: { paymentState: PAYMENT_STATE.unpaid },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('subscriptionSales.discountsGiven')}
              value={formatCurrency(Math.abs(money?.discountsGiven ?? 0))}
              tooltip={t('subscriptionSales.discountsGivenTooltip')}
              onClick={() =>
                setDrill({
                  title: t('subscriptionSales.discountsGiven'),
                  patch: { paymentState: PAYMENT_STATE.belowList },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('subscriptionSales.unredeemedGifts')}
              value={formatCurrency(money?.unredeemedGiftRevenue)}
              tooltip={t('subscriptionSales.unredeemedGiftsTooltip')}
              onClick={() => setGiftsOpen(true)}
            />
          </StatGroup>
        </TabsContent>

        {/* =================================================== plans ==== */}
        <TabsContent value="plans" className="space-y-3">
          {loading && (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-28 rounded-xl" />
              ))}
            </div>
          )}

          {dashboard.isSuccess && planRows.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={busy} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
                {t('subscriptionSales.noPlans')}
              </p>
            </div>
          )}

          {planRows.length > 0 && (
            <>
              {/* Pinned above the list rather than a footer row. */}
              <div className="bg-card border-l-primary relative space-y-2 rounded-xl border border-l-[3px] p-3">
                <BusyOverlay show={busy} size="sm" />
                <SectionTitle title={t('subscriptionSales.total')} />
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                  <Cell
                    label={t('subscriptionSales.sold')}
                    value={formatNumber(totals.sold)}
                  />
                  <Cell
                    label={t('subscriptionSales.activeColumn')}
                    value={formatNumber(totals.active)}
                  />
                  <Cell
                    label={t('subscriptionSales.paidOnline')}
                    value={formatNumber(totals.paidOnline)}
                  />
                  <Cell
                    label={t('subscriptionSales.paidOffline')}
                    value={formatNumber(totals.paidOffline)}
                  />
                  <Cell
                    label={t('subscriptionSales.sponsored')}
                    value={formatNumber(totals.sponsored)}
                  />
                  <Cell
                    label={t('subscriptionSales.unbilled')}
                    value={formatNumber(totals.unpaid)}
                  />
                  <Cell
                    label={t('subscriptionSales.collected')}
                    value={formatCurrency(totals.collected)}
                  />
                  <Cell
                    label={t('subscriptionSales.unbilledValue')}
                    value={formatCurrency(totals.unbilled)}
                  />
                </div>
              </div>

              <div className="relative divide-y rounded-xl border">
                <BusyOverlay show={busy} />
                {planRows.map((row) => (
                  <div key={row.subscriptionId} className="space-y-3 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <LocalizedName
                          nameEn={row.nameEn}
                          nameEs={row.nameEs}
                          internalName={row.internalName}
                        />
                        <p className="text-muted-foreground truncate text-xs">
                          {localizedName(
                            {
                              nameEn: row.locationNameEn,
                              nameEs: row.locationNameEs,
                            },
                            language
                          )}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-semibold tabular-nums">
                          {formatCurrency(row.collected)}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {t('subscriptionSales.collected').toLowerCase()}
                        </p>
                      </div>
                    </div>

                    {/* Only a capped plan has a cap to show against. */}
                    {(row.limit ?? 0) > 0 && (
                      <Meter
                        label={t('subscriptionSales.soldOfLimit', {
                          sold: formatNumber(row.acquired),
                          limit: formatNumber(row.limit),
                        })}
                        value={`${Math.min(
                          Math.round(((row.acquired ?? 0) / (row.limit || 1)) * 100),
                          100
                        )}%`}
                        percent={Math.min(
                          Math.round(((row.acquired ?? 0) / (row.limit || 1)) * 100),
                          100
                        )}
                      />
                    )}

                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                      <Cell
                        label={t('subscriptionSales.sold')}
                        value={formatNumber(row.sold)}
                      />
                      <Cell
                        label={t('subscriptionSales.activeColumn')}
                        value={formatNumber(row.active)}
                      />
                      <Cell
                        label={t('subscriptionSales.paidOnline')}
                        value={formatNumber(row.paidOnline)}
                      />
                      <Cell
                        label={t('subscriptionSales.paidOffline')}
                        value={formatNumber(row.paidOffline)}
                      />
                      <Cell
                        label={t('subscriptionSales.sponsored')}
                        value={formatNumber(row.sponsored)}
                      />
                      <Cell
                        label={t('subscriptionSales.unbilled')}
                        value={formatNumber(row.unpaid)}
                      />
                      <Cell
                        label={t('subscriptionSales.unbilledValue')}
                        value={formatCurrency(row.unbilled)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <SubscriptionRowsSheet
        model={model}
        patch={drill?.patch ?? null}
        title={drill?.title ?? ''}
        link={
          drill && drill.linkable !== false
            ? subscriptionsLink(drill.patch)
            : null
        }
        open={drill !== null}
        onOpenChange={(next) => {
          if (!next) setDrill(null);
        }}
      />

      <GiftRowsSheet
        locationId={model.locationId}
        startDate={period.startDate}
        endDate={period.endDate}
        open={giftsOpen}
        onOpenChange={setGiftsOpen}
      />
    </div>
  );
}
